import { useMemo, useState } from "react";

import { useGetRolesNew } from "@vega-api/generated/auth-permission-v1/auth-permission-v1";
import { RoleContext } from "@vega-api/generated/types/roleContext";
import { type RoleNew } from "@vega-api/generated/types/roleNew";
import { SegmentedTabs } from "@vega-core/components/SegmentedTabs/SegmentedTabs";
import { Skeleton } from "@vega-core/components/shadcn/skeleton";
import { buildTimeFlags } from "@vega-core/utils/buildTimeFlags";
import { convertToTitleCase } from "@vega-core/utils/formatString";

export enum RolePhase {
  Offering = "offerings",
  Investment = "investment",
}

type RoleConfig = { title: string; description: string };

type RolesConfig = Record<RolePhase, RoleConfig[]>;

export const rolePhaseTabOptions = [
  { id: RolePhase.Offering, label: "Offering roles" },
  ...(buildTimeFlags.reporting_settings_investments
    ? [{ id: RolePhase.Investment, label: "Investment roles" }]
    : []),
];

const toRoleConfig = (
  role: Pick<RoleNew, "extendedDescription" | "name">,
): RoleConfig => ({
  title: convertToTitleCase(role.name),
  description: role.extendedDescription ?? "",
});

export const useRolesConfig = (): {
  rolesConfig: RolesConfig;
  isLoading: boolean;
} => {
  const { data: roles = [], isLoading } = useGetRolesNew(
    { context: RoleContext.INVESTOR_PORTAL },
    { query: { select: (r) => r.data ?? [] } },
  );

  const rolesConfig = useMemo(
    () =>
      roles.reduce<RolesConfig>(
        (groupedRoles, role) => {
          const config = toRoleConfig(role);
          const includesPre = role.contexts.includes(
            RoleContext.PRE_INVESTMENT,
          );
          const includesPost = role.contexts.includes(
            RoleContext.POST_INVESTMENT,
          );

          return {
            [RolePhase.Offering]: includesPre
              ? [...groupedRoles[RolePhase.Offering], config]
              : groupedRoles[RolePhase.Offering],
            [RolePhase.Investment]: includesPost
              ? [...groupedRoles[RolePhase.Investment], config]
              : groupedRoles[RolePhase.Investment],
          };
        },
        { [RolePhase.Offering]: [], [RolePhase.Investment]: [] },
      ),
    [roles],
  );

  return { rolesConfig, isLoading };
};

export const RoleConfigList = ({
  roles,
  isLoading,
}: {
  roles: RoleConfig[];
  isLoading?: boolean;
}) => {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 p-4">
        {["title", "description", "details"].map((key) => (
          <Skeleton className="w-full h-4 rounded" key={key} />
        ))}
      </div>
    );
  }

  return roles.map(({ title, description }) => (
    <div
      className="flex p-4 gap-2 border-border/primary border-t first:border-t-0"
      key={title}
    >
      <div className="text-text/primary body-base-semibold w-1/4 text-start flex items-start">
        {title}
      </div>
      <div className="text-text/primary body-small-regular w-3/4">
        {description}
      </div>
    </div>
  ));
};

export const RoleTabs = () => {
  const [tab, setTab] = useState<RolePhase>(RolePhase.Offering);
  const { rolesConfig, isLoading } = useRolesConfig();

  return (
    <div className="flex flex-col gap-6 rounded-lg bg-background/secondary p-4">
      <SegmentedTabs
        activeTab={tab}
        noWrapText
        setActiveTab={setTab}
        tabs={rolePhaseTabOptions}
      />

      <div>
        <RoleConfigList isLoading={isLoading} roles={rolesConfig[tab]} />
      </div>
    </div>
  );
};
